/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import StoryblokClient, { Stories, StoryData } from 'storyblok-js-client';
import { StoryblokService } from '../../services/storyblok';
import { Props } from '../types';
import { PageStateContext } from '../../services/pageStore';

type FilterGroup = {
  uuid: string;
  name: string;
  items: Array<FilterItem>;
}

type FilterItem = {
  uuid: string;
  name: string;
  parentId: string;
}

type ProductCatalogFields = {
  nbr_show_more: string;
  txt_filter: string;
  txt_active_filter: string;
  txt_delete_filter: string;
  txt_found_products: string;
  txt_all_products: string;
  txt_open_product: string;
  btn_show_more: string;
  preselection_filter: { selectedItems };
  btn_mobile_filter_setting: string;
  btn_mobile_filter_close: string;
  list_view: boolean;
}

export const ProductCatalog = ({ blok }: Props<ProductCatalogFields>): JSX.Element => {
  const { storyblok } = useStaticQuery(graphql`
    {
    storyblok {
      items: ProductfilterItems(per_page: 30) {
        items {
          full_slug
          content {
            txt_filtername
          }
          uuid
          parent_id
        }
      }
      groups: ProductfiltergroupItems {
        items {
          uuid
          id
          content {
            txt_filtergroup
          }
          parent_id
        }
      }
    }
  }
  `);
  const { settings } = useContext(PageStateContext);
  const storyblokJsClient = useRef(null);
  const fetchingData = useRef<boolean>(false);
  const isMoreButtonVisible = useRef<boolean>(false);
  const moreButton = useRef<HTMLElement | null>(null);
  const itemsToShow = Number(blok.nbr_show_more);
  const totalProducts = useRef<number>(0);
  const currentPage = useRef<number>(1);
  const filter = useRef([]);
  const visibleGroups: string[] = blok.filter.filter((g) => g.filter).map((g) => g.filter);
  const collapsedGroups: string[] = blok.filter
    .filter((g) => g.filter && g.accordion_state).map((g) => g.filter);
  const preselectedFilter = blok.preselection_filter.selectedItems;
  const preselectionEnabled = blok.preselection_option;
  const [products, setProducts] = useState([]);
  const overlayFilterShown = useRef(false);
  const [nonEmptyFilters, setNonEmptyFilters] = useState([]);
  const [gridLayout, setGridLayout] = useState(!blok.list_view);
  const groupFilters = (prefix): object => filter.current
    .reduce((acc, i) => {
      const key = prefix + i.parentId;
      if (!acc[key]) {
        acc[key] = { in_array: '' };
      }
      acc[key].in_array += `${i.uuid},`;
      return acc;
    }, {});

  const doFetchProducts = (page: number, perPage: number): Promise<Stories> => storyblokJsClient.current?.getStory('', {
    per_page: perPage,
    page,
    sort_by: 'first_published_at:desc',
    filter_query: {
      component: {
        in: 'page-product',
      },
      authorized_roles: {
        is: '[]',
      },
      ...groupFilters('filter_assignment.selectedItems.'),
    },
  });

  const fetchAllProducts = async (): Promise<void> => {
    const perPage = 100;
    const requests = [];
    const initial = await storyblokJsClient.current?.client.head('cdn/stories/', {
      params: {
        token: StoryblokService.getConfig().options.accessToken as string,
        per_page: 1,
        page: 1,
        'filter_query[component][in]': 'page-product',
        'filter_query[authorized_roles][is]': '[]',
      },
    });
    const totalPages = Math.ceil(initial.headers.total / perPage);
    for (let i = 1; i <= totalPages; i += 1) {
      requests.push(doFetchProducts(i, perPage));
    }
    const responses = await Promise.all(requests);
    let allUuids = responses.reduce((acc, r) => {
      const arr = r.data.stories.reduce((rAcc, s) => {
        if (s.content && s.content.filter_assignment) {
          const uuids = Object.values(s.content.filter_assignment.selectedItems).flat(1);
          return [...rAcc, ...uuids];
        }
        return rAcc;
      }, []);
      return [...acc, ...arr];
    }, []);
    allUuids = [...new Set(allUuids)];
    // uncheck filter items which has no products in it
    filter.current = filter.current.filter((i) => allUuids.includes(i.uuid));
    setNonEmptyFilters(allUuids);
  };

  const fetchProducts = async (): Promise<{ stories: StoryData[] }> => {
    fetchingData.current = true;
    const { data, total } = await doFetchProducts(currentPage.current, itemsToShow);
    fetchingData.current = false;
    totalProducts.current = total;
    isMoreButtonVisible.current = products?.length < totalProducts.current;

    return data;
  };

  const isFilterEmpty = (uuid): boolean => !nonEmptyFilters.includes(uuid);

  const loadFirstPage = (): void => {
    currentPage.current = 1;
    fetchProducts().then((p) => {
      currentPage.current += 1;
      setProducts([...p.stories]);
    });
  };
  const onSetFilter = (item: FilterItem, loadFirstPageEnabled = true): void => {
    if (isFilterEmpty(item.uuid)) { return; }
    const index = filter.current.findIndex((i) => i.uuid === item.uuid);
    if (index === -1) {
      filter.current = [...filter.current, item];
    } else {
      const copy = [...filter.current];
      copy.splice(index, 1);
      filter.current = copy;
    }
    if (loadFirstPageEnabled) {
      loadFirstPage();
    }
    fetchAllProducts();
  };

  const storyToFilter = (story): FilterItem => {
    if (!story) { return null; }
    return { uuid: story.uuid, name: story.content.txt_filtername, parentId: story.parent_id };
  };

  const groupFilterItems = (groups, items): FilterGroup[] => {
    const groupedData = {};
    groups.filter((g) => visibleGroups.includes(g.uuid))
      .forEach((g) => {
        groupedData[g.parent_id] = { uuid: g.uuid, name: g.content.txt_filtergroup, items: [] };
      });
    items.forEach((i) => {
      if (groupedData[i.parent_id]) {
        groupedData[i.parent_id].items.push(storyToFilter(i));
      }
    });
    return Object.values<FilterGroup>(groupedData)
      .sort((a, b) => visibleGroups.indexOf(a.uuid) - visibleGroups.indexOf(b.uuid));
  };

  const groupedFilterData: FilterGroup[] = groupFilterItems(
    storyblok.groups.items, storyblok.items.items,
  );
  const loadMore = (): void => {
    if (fetchingData.current) return;
    fetchProducts().then((p) => {
      currentPage.current += 1;
      setProducts([...products, ...p.stories]);
    });
  };
  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
    filter.current = storyblok.items.items
      .filter((i) => preselectionEnabled
        && preselectedFilter[i.parent_id]
        && preselectedFilter[i.parent_id].includes(i.uuid))
      .map((i) => storyToFilter(i));

    currentPage.current = 1;
    loadMore();
    fetchAllProducts();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    moreButton.current?.addEventListener('rocheButtonClick', loadMore);
    return (): void => moreButton.current?.removeEventListener('rocheButtonClick', loadMore);
  });

  useEffect(() => {
    isMoreButtonVisible.current = products?.length < totalProducts.current;
  }, [products]);

  const clearFilter = (index?: number): void => {
    if (typeof index === 'undefined') {
      filter.current = [];
    } else {
      filter.current.splice(index, 1);
    }
    loadFirstPage();
  };

  const handleToggle = (): void => {
    overlayFilterShown.current = !overlayFilterShown.current;
    setProducts([...products]); // just for force re-render component
  };

  const viewSwitcher = (): JSX.Element => (
    <div style={{ display: 'flex', gap: '10px', marginRight: '10px' }}>
      <roche-icon icon="burger-menu" onClick={() => setGridLayout(false)} style={{ cursor: gridLayout ? 'pointer' : 'initial', opacity: gridLayout ? '1' : '0.5' }} />
      <roche-icon icon="overview" onClick={() => setGridLayout(true)} style={{ cursor: !gridLayout ? 'pointer' : 'initial', opacity: !gridLayout ? '1' : '0.5' }} />
    </div>
  );

  const filterComponent = (isMobile: boolean): JSX.Element => (
    <div>
      {groupedFilterData.map((g) => (
        <roche-accordion-tabs type="accordion" key={g.uuid} start-position={!isMobile && !collapsedGroups.includes(g.uuid) ? 0 : null} style={{ marginTop: 0 }}>
          <roche-accordion-tabs-item headline={g.name} style={{ padding: 0, margin: 0 }} type="accordion" enable-jumpmark="false">
            <roche-list>
              <ul slot="items" style={{ margin: '0' }}>
                {g.items.map((item, j) => (
                  <li key={j} style={{
                    background: '#f2f2f2', border: '1px solid #ebebeb', padding: '5px 0', opacity: isFilterEmpty(item.uuid) ? '0.5' : '1',
                  }}>
                    <label>
                      <input checked={filter.current.some((f) => f.uuid === item.uuid)} onChange={() => onSetFilter(item)} type="checkbox" style={{ margin: '0 1rem' }} />
                      {item.name}
                    </label>
                  </li>
                ))}
              </ul>
            </roche-list>
          </roche-accordion-tabs-item>
        </roche-accordion-tabs>
      ))}
    </div>
  );
  return (
    <roche-grid layout="4-8" visibility={'is-hidden-mobile'} full-width>
      <div slot="left">
        <roche-headline level={3} headline={blok?.txt_filter || 'Filter'} style={{ marginTop: 0 }} />
        {filterComponent(false)}
      </div>
      <div slot="right">
        <roche-visibility classes="is-hidden-mobile">
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid #ccc',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px',
            }}>
              <div style={{ fontSize: '1rem' }}>{blok?.txt_active_filter}</div>
              {filter?.current?.map((f, index) => (
                <roche-generic-link key={f.uuid} style={{
                  padding: '0 5px 3px', cursor: 'pointer', margin: 0, whiteSpace: 'nowrap', background: '#E9F1FB', borderRadius: '7px', color: '#333333',
                }} text={f.name} icon="close-bold" size="small" icon-position="after" onClick={() => clearFilter(index)} />
              ))}
            </div>
            <roche-generic-link style={{ cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }} text={blok.txt_delete_filter} icon="close-bold" size="small" onClick={() => clearFilter()} />
          </div>
        </roche-visibility>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem' }}>
          {filter.current && filter.current.length > 0 && (
            <div>{totalProducts?.current} {blok.txt_found_products}</div>
          )}
          {filter.current.length === 0 && (
            <div>{blok.txt_all_products}</div>
          )}
          <roche-visibility classes="is-hidden-mobile">{viewSwitcher()}</roche-visibility>
        </div>
        <roche-visibility classes="is-hidden-tablet">
          {!overlayFilterShown.current && <roche-generic-link text={blok.btn_mobile_filter_setting} icon="cog" size="large" onClick={() => handleToggle()} style={{
            border: '1px solid #0066cc', textAlign: 'center', margin: '15px 1px', padding: '5px 0', cursor: 'pointer', textDecoration: 'none', width: '100%',
          }} />}
          {overlayFilterShown.current && (
            <div style={{
              background: 'rgb(255 255 255 / 92%)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, padding: '20px', zIndex: 11111, overflowY: 'auto',
            }}>
              <roche-generic-link text={blok.btn_mobile_filter_close} icon="close" icon-position="after" icon-color="#999" size="huge" onClick={() => handleToggle()} style={{
                color: '#000', margin: '1px', padding: '5px 0', cursor: 'pointer', textDecoration: 'none', width: '100%', textAlign: 'right',
              }} />
              <roche-headline level={3} headline="Filter" style={{ marginTop: 0 }} />
              {filterComponent(true)}
            </div>)}
        </roche-visibility>
        {!products.length && !fetchingData.current && <div style={{ textAlign: 'center' }}>No data</div>}
        <roche-columns center-children={'false'} multiple-rows={true}>
          {products.map((item, i) => (
            <roche-column key={i} span={gridLayout ? 4 : 12} debug={false}>
              <roche-teaser description={item.content?.meta_tags?.description}
                teaser-url={`/${item.full_slug}`}
                headline={item.content.teaser_title || item.name} layout={gridLayout ? 'vertical' : 'horizontal'}>
                <roche-image slot="media" src={item.content.teaser_image.filename || settings?.press_articles_teaser_fallback_image?.filename} />
                <roche-generic-link slot="link" text={blok.txt_open_product} url={`/${item.full_slug}`} field-type="multilink" icon="chevron-right-bold" size="large" iconPosition="before" />
              </roche-teaser>
            </roche-column>
          ))}
        </roche-columns>
        <div
          style={{
            display: isMoreButtonVisible.current ? 'flex' : 'none',
            justifyContent: 'center',
          }}>
          <roche-button ref={moreButton} text={blok.btn_show_more} />
        </div>
      </div>
    </roche-grid>
  );
};
